/**
 * Design Wall system — data model + path sampler.
 *
 * A "decor wall group" places one decorative wall mesh and repeats it N times
 * along an axis (X / Y / Z=time), forming a line. The line can be shaped by a
 * curve preset with an amplitude, and its ends can taper (the extreme walls
 * scale down). Groups are edited in the DesignWall panel and rendered by the
 * DesignShapes three component. Persisted per-map in a localStorage draft (like
 * flash-light events), since SynthVR has no backend edit-save for this layer.
 *
 * Curve presets come in three flavours:
 *   • bent line   — a per-position perpendicular displacement of the walked line
 *                   (arc / valley / s-curve / wave / ripple / zigzag / switchback
 *                   / saw / steps / peak / bell).
 *   • 2D shape    — a parametric loop in the X/Y plane, sized by amplitude
 *                   (circle / spiral). Checking the Z axis extrudes it through
 *                   time (a ring → cylinder, a spiral → helix).
 *   • custom      — a Catmull-Rom path through user-placed control nodes that
 *                   are dragged in the 3D scene (draggable gizmo handles) or
 *                   nudged from the panel. The nodes own all three local coords.
 */

import * as THREE from 'three';
import type { ObstacleShape } from '../store/editorSettings';

export type DesignAxis = 'x' | 'y' | 'z';
export type DesignTip = 'straight' | 'taper';
export type DesignCurve =
  // bent line (perpendicular displacement)
  | 'line' | 'arc' | 'valley' | 's-curve' | 'wave' | 'ripple'
  | 'zigzag' | 'switchback' | 'saw' | 'steps' | 'peak' | 'bell'
  // 2D parametric shapes (X/Y loop, sized by amplitude)
  | 'circle' | 'spiral'
  // user-drawn control-node path
  | 'custom';
/** Symmetry effect applied to the whole line after it's sampled. */
export type DesignMirror = 'none' | 'x' | 'y' | 'kaleidoscope';
export type DesignShapeStyle = 'solid' | 'wireframe';
// How a gradient distributes its colour across the walls:
//   order  — along the generation order (index) [default, legacy behaviour]
//   x/y    — along the wall's world X / Y position
//   z      — along the beat (depth / time)
//   radial — by distance from the group's centre (great for kaleidoscope)
export type DesignGradientDir = 'order' | 'x' | 'y' | 'z' | 'radial';

// ── Animation ────────────────────────────────────────────────────────────────
// A live, per-frame deformation of the whole wall art (positions/scale/rotation)
// layered on top of the static sampled pose. Purely a runtime visual — never
// baked into the flat .synth obstacles (the game has no animation channel), but
// the config rides along in the SynapseDecorWalls embed for a modded reader.
export type DesignAnimPreset =
  | 'none'
  // scale-pumped
  | 'pulse' | 'breathe' | 'shockwave' | 'flicker'
  // position-deformed
  | 'wave' | 'tentacle' | 'ripple' | 'jelly'
  // rotation-driven
  | 'spin' | 'orbit' | 'twist' | 'sway';
/** What drives the animation: a free-running clock, the music's live energy, or
 *  the chart's note hits (an impulse each time a note passes the trigger). */
export type DesignAnimSync = 'time' | 'music' | 'notes';
/** How the beat/music reaction is distributed across the walls: all together, or
 *  staggered so it reacts by segments (random / sequential / radial). */
export type DesignAnimDist = 'uniform' | 'random' | 'sequence' | 'radial';
export const DESIGN_ANIM_PRESETS: DesignAnimPreset[] = [
  'none', 'pulse', 'breathe', 'shockwave', 'flicker',
  'wave', 'tentacle', 'ripple', 'jelly',
  'spin', 'orbit', 'twist', 'sway',
];
export const DESIGN_ANIM_DISTS: DesignAnimDist[] = ['uniform', 'random', 'sequence', 'radial'];

export interface DesignAnimConfig {
  preset: DesignAnimPreset;
  sync: DesignAnimSync;   // time = free clock · music = audio energy · notes = note hits
  speed: number;         // animation rate
  amount: number;        // intensity / amplitude (the "Strength" slider)
  phase: number;         // spatial spread across the art (the "Spread" slider)
  // ── Beat reaction (Notes sync): a shaped in/out envelope per hit ──
  attack?: number;       // beats — the "in" rise time (accelerating)
  release?: number;      // beats — the "out" fall time (decelerating)
  easeIn?: number;       // >=~0.3 — attack acceleration exponent (higher = sharper in)
  easeOut?: number;      // >=~0.3 — release deceleration exponent (higher = sharper out)
  // ── Reaction distribution (Notes / Music sync): stagger it across the walls ──
  dist?: DesignAnimDist;  // uniform (whole bar) · random / sequence / radial (segments)
  stagger?: number;      // beats — how spread out the per-segment reaction is
  // ── Tentacle motion style ──
  tentacleMode?: 'sway' | 'radial';   // sway = side-to-side (default); radial =
                                       // expand/contract from the centre (open & close)
}

/** The wall meshes available as decor styles (reuse the game obstacle shapes). */
export const DESIGN_MESHES: ObstacleShape[] = ['regular', 'diagonal', 'center', 'square', 'triangle'];
export const DESIGN_CURVES: DesignCurve[] = [
  'line', 'arc', 'valley', 's-curve', 'wave', 'ripple', 'zigzag', 'switchback',
  'saw', 'steps', 'peak', 'bell', 'circle', 'spiral', 'custom',
];
/** The 2D parametric-shape presets (laid out as an X/Y loop, sized by amplitude). */
export function isShapeCurve(c: DesignCurve): boolean {
  return c === 'circle' || c === 'spiral';
}
export const DESIGN_AXES: DesignAxis[] = ['x', 'y', 'z'];
export const DESIGN_MIRRORS: DesignMirror[] = ['none', 'x', 'y', 'kaleidoscope'];

/** One control node of a `custom` curve. Local coordinates, centred on the
 *  origin like the rest of a group's build space: x/y in app units, z is a
 *  BEAT offset from the group's anchor beat. */
export interface DesignPathNode { x: number; y: number; z: number; }

/** Starter control path for a fresh `custom` curve — a gentle S in the X/Y
 *  plane, centred on the origin so Global Position/Rotation still move it as a
 *  unit and it's immediately visible + draggable. */
export const DEFAULT_CUSTOM_PATH: DesignPathNode[] = [
  { x: -2, y: -0.8, z: 0 },
  { x: -0.7, y: 0.9, z: 0 },
  { x: 0.7, y: -0.9, z: 0 },
  { x: 2, y: 0.8, z: 0 },
];
export function makeDefaultCustomPath(): DesignPathNode[] {
  return DEFAULT_CUSTOM_PATH.map(p => ({ ...p }));
}

/** Sentinel id for the primary shape's "row"/gizmo — it isn't a real
 *  DesignMesh (it's g.shape/color/etc.), but the panel's Parts list and the
 *  scene's move-gizmo both treat it the same way as an extra part. */
export const PRIMARY_MESH_ID = '__primary__';

export interface DesignShape {
  id: string;
  name: string;            // user-editable label shown in the Shape Designer list
  beat: number;            // anchor beat (timeline position of the group's centre)
  shape: ObstacleShape;    // which wall mesh
  axes: DesignAxis[];       // repeat direction(s) — x/y are spatial, z is time
                           // (beats); when more than one is checked the line
                           // moves diagonally across ALL of them at once.
  count: number;           // number of walls (>= 1)
  spacing: number;         // step between walls (x/y: app units; z: beats)
  curve: DesignCurve;       // path shape
  amplitude: number;       // perpendicular displacement for curved presets (app units);
                           // for the 2D shape presets (circle/spiral) it's the radius.
  customPath?: DesignPathNode[]; // control nodes for the `custom` curve (Catmull-Rom
                           // through them). Local, centred on the origin. Seeded on
                           // demand (makeDefaultCustomPath) when the user picks Custom.
  tip: DesignTip;           // end termination
  taperStrength: number;   // 0..1 — how small the extreme walls get when tapered
  taperLength?: number;    // 0..1 — how much of each half tapers (1 = full ramp
                           // from centre to ends; smaller = shorter/sharper tip). Default 1.
  mirror: DesignMirror;     // symmetry effect over the sampled line
  mirrorCount: number;     // kaleidoscope repetitions (radial copies, >= 2)
  repeatCount: number;     // copies of the WHOLE module/pattern (>= 1)
  repeatSpacing: number;   // gap between module copies (x/y: app units; z: beats)
  repeatAxis: DesignAxis;   // offset direction for the module copies
  color: string;           // fill / outline tint (the START colour when gradient)
  gradient: boolean;       // fade the outline colour across the sequence
  colorEnd: string;        // outline colour of the LAST wall when gradient is on
  gradientDir?: DesignGradientDir; // how the gradient distributes (default 'order')
  style?: DesignShapeStyle;  // the PRIMARY shape's render style: 'solid' (filled
                           // panel) | 'wireframe' (edges only) — matches the game's
                           // two wall looks. Per-shape (each part has its own
                           // `style` too), so one art can mix solid + wireframe.
                           // Default 'solid'. (Over the Quest cap the whole art is
                           // force-wireframed regardless.)
  anim?: DesignAnimConfig;  // live per-frame animation of the whole art (undefined /
                           // preset 'none' = static). Runtime visual only; not baked.
  scale: number;           // base wall scale multiplier (uniform X/Y/Z)
  scaleZ?: number;         // EXTRA depth multiplier on the local Z axis only
                           // (stretch/flatten the wall's thickness); default 1.
  rotation: number;        // Z-rotation of every wall (degrees) — spins in the wall's own plane
  rotationX: number;       // X-rotation (degrees) — tilts the wall forward/back
  rotationY: number;       // Y-rotation (degrees) — turns the wall left/right
  twist: number;           // extra rotation spread 0→twist along the line (deg) — a graded per-object twist/helix (Z only)
  offsetX?: number;        // PRIMARY shape's own local offset within the composite
  offsetY?: number;        // (like a part's offset) — lets shape 1 be nudged/aligned,
  offsetZ?: number;        // e.g. to centre a manual mirror. Default 0. Not Global Position.
  originX: number;         // group centre X offset (app units) — Global Position;
                           // translates the WHOLE finished shape (post-symmetry).
  originY: number;         // group centre Y offset (app units) — same, Y axis.
  spreadX?: number;        // pre-symmetry local offset (app units) — with a
                           // mirror/kaleidoscope active this pushes each
                           // reflected copy apart from the group's own centre
                           // (an "expand" control), independent of Global
                           // Position. With no symmetry it's equivalent to
                           // Move X (harmless — just an extra nudge knob).
  spreadY?: number;        // same, Y axis.
  spreadZ?: number;        // same, but beat/time — symmetry never touches beat
                           // so this always behaves like Move Z.
  originRotX?: number;     // Global Rotation — turn the whole PLACED wall art in
  originRotY?: number;     // 3D around its placed centre (originX/Y, g.beat), as
  originRotZ?: number;     // the final step. Companion to Move X/Y/Z (deg).
  spreadRotX?: number;     // rotate the WHOLE assembled arrangement (positions +
  spreadRotY?: number;     // orientation) rigidly around the group's own centre,
  spreadRotZ?: number;     // post-symmetry (deg). Distinct from the per-wall
                           // Rotation X/Y/Z which orient each mesh in place and
                           // are negated by mirror symmetry. Z (in-plane spin) is
                           // exact; X/Y tilt the piece into depth.
  parts: DesignMesh[];  // EXTRA shapes riding along with the primary shape, so
                           // one "wall art" can combine several obstacle meshes
                           // that move/repeat/mirror together as a single unit.
  linkedShape: boolean;    // when true, every part's Scale/Rotation(X/Y/Z) is
                           // locked to the primary's — independent of linkedColor.
  linkedColor: boolean;    // when true, every part's Color/Gradient/End is
                           // locked to the primary's — independent of linkedShape.
                           // Either way, position (offsetX/Y/Beat) and Shape
                           // (the actual mesh) always stay independent.
  shapesIndependent?: boolean; // when true, the shapes are DISCONNECTED: each part
                           // is sampled with its OWN Frequency/Effects/Expand
                           // (part override fields, falling back to the group's).
                           // Default (false/undefined) = ANCHORED: parts ride the
                           // primary's pattern as one composite (the normal case).
  hidden?: boolean;        // when true, the PRIMARY shape's own layer is skipped
                           // at render time (parts are unaffected — each hides
                           // independently via its own `hidden`).
}

/** An extra shape combined into a wall art alongside the primary `shape`. Rides
 *  at the SAME repeat slots as the primary (same Frequency/Effects/Position),
 *  offset by (offsetX, offsetY, offsetBeat) and styled independently. */
export interface DesignMesh {
  id: string;
  shape: ObstacleShape;
  color: string;
  gradient: boolean;    // fade this part's own outline colour across the sequence
  colorEnd: string;      // outline colour of the LAST wall when gradient is on
  style?: DesignShapeStyle; // this part's own render style (solid | wireframe),
                          // independent of the primary + other parts. Default 'solid'.
  anim?: DesignAnimConfig; // this part's OWN animation — used only when the shapes
                          // are DISCONNECTED (shapesIndependent); falls back to the
                          // group's anim when unset. Anchored = shares the group's.
  scale: number;        // multiplies the wall art's base Scale
  scaleZ?: number;      // extra depth (local Z) multiplier; default 1
  rotation: number;     // added on top of each slot's Z rotation
  rotationX: number;    // added on top of each slot's X rotation
  rotationY: number;    // added on top of each slot's Y rotation
  offsetX: number;      // local offset from the primary slot (app units)
  offsetY: number;
  offsetBeat: number;   // local time offset from the primary slot (beats)
  hidden?: boolean;     // when true, this part's own layer is skipped at render time
  // ── Per-part Frequency/Effects/Expand OVERRIDES — used ONLY when the group is
  //    DISCONNECTED (shapesIndependent). Each is optional and falls back to the
  //    group's value when unset, so disconnecting initially changes nothing. ──
  axes?: DesignAxis[];
  count?: number;
  spacing?: number;
  curve?: DesignCurve;
  amplitude?: number;
  customPath?: DesignPathNode[];
  tip?: DesignTip;
  taperStrength?: number;
  taperLength?: number;
  twist?: number;
  mirror?: DesignMirror;
  mirrorCount?: number;
  repeatCount?: number;
  repeatSpacing?: number;
  repeatAxis?: DesignAxis;
  spreadX?: number;
  spreadY?: number;
  spreadZ?: number;
  spreadRotX?: number;
  spreadRotY?: number;
  spreadRotZ?: number;
}

/** One expanded wall — a concrete transform in local (x, y, beat) space. */
export interface DesignMeshInstance {
  x: number;
  y: number;
  beat: number;
  scale: number;
  scaleZ: number;      // extra local-Z (depth) multiplier on top of `scale`
  rotation: number;    // Z
  rotationX: number;
  rotationY: number;
}

// Reused temps for the Expand-rotation pass (avoid per-instance allocation).
const _spreadEuler = new THREE.Euler();
const _spreadVec = new THREE.Vector3();

/** Which axes a group repeats along — reads the (current) `axes` array, or
 *  falls back to a legacy single `axis` field / 'x' for drafts saved before
 *  multi-axis support existed. Always returns at least one axis. */
export function resolveAxes(g: { axes?: DesignAxis[]; axis?: DesignAxis }): DesignAxis[] {
  if (Array.isArray(g.axes) && g.axes.length) return g.axes;
  if (g.axis) return [g.axis];
  return ['x'];
}

/** Perpendicular displacement (−1..1-ish) for a bent-line curve preset at
 *  position t (0..1). Shape/custom curves are handled separately by the sampler. */
function curveDisplacement(curve: DesignCurve, t: number): number {
  switch (curve) {
    case 'arc':        return Math.sin(Math.PI * t);                       // single hump: 0→1→0
    case 'valley':     return -Math.sin(Math.PI * t);                      // single dip: 0→−1→0
    case 's-curve':    return Math.sin(2 * Math.PI * t) * 0.5;             // one full S
    case 'wave':       return Math.sin(3 * Math.PI * t) * 0.5;             // 1.5 waves
    case 'ripple':     return Math.sin(6 * Math.PI * t) * 0.5;             // 3 fast waves
    case 'zigzag':     return (Math.abs(((t * 4) % 2) - 1) * 2 - 1) * 0.5; // triangle wave
    case 'switchback': return (Math.abs(((t * 8) % 2) - 1) * 2 - 1) * 0.5; // tighter triangle
    case 'saw':        return t * 2 - 1;                                   // linear ramp (shear)
    case 'steps':      return (Math.round(t * 5) / 5) * 2 - 1;             // staircase
    case 'peak':       return 1 - Math.abs(t * 2 - 1);                     // linear tent: 0→1→0
    case 'bell':       return Math.exp(-Math.pow((t - 0.5) * 5, 2));       // gaussian bump
    case 'line':
    default:           return 0;
  }
}

/** Unit-ish (x, y) for a parametric 2D shape preset at t (0..1); scaled by the
 *  group's amplitude (= radius) in the sampler. Centred on the origin. */
function curveShapeXY(curve: DesignCurve, t: number): { x: number; y: number } {
  switch (curve) {
    case 'circle': {
      const a = 2 * Math.PI * t;
      return { x: Math.sin(a), y: -Math.cos(a) };          // starts at the bottom, goes round
    }
    case 'spiral': {
      const turns = 3;
      const a = 2 * Math.PI * turns * t;
      return { x: Math.cos(a) * t, y: Math.sin(a) * t };   // radius grows 0 → 1 over the turns
    }
    default:
      return { x: 0, y: 0 };
  }
}

/** Sample `count` evenly-parametrised points along a smooth Catmull-Rom curve
 *  through the custom control nodes (falls back to the default starter path when
 *  fewer than two nodes exist). Returns local (x, y, z=beat-offset) points. */
export function sampleCustomPath(nodes: DesignPathNode[] | undefined, count: number): DesignPathNode[] {
  const pts = (nodes && nodes.length >= 2) ? nodes : DEFAULT_CUSTOM_PATH;
  const n = Math.max(1, Math.round(count));
  const curve = new THREE.CatmullRomCurve3(
    pts.map(p => new THREE.Vector3(p.x, p.y, p.z)), false, 'catmullrom', 0.5,
  );
  const out: DesignPathNode[] = [];
  for (let i = 0; i < n; i++) {
    const v = curve.getPoint(n > 1 ? i / (n - 1) : 0);
    out.push({ x: v.x, y: v.y, z: v.z });
  }
  return out;
}

/** Apply the symmetry effect to a sampled line, expanding it into extra copies.
 *  X/Y mirror reflect across the tunnel centre (x = 0 / world-y = NOTE_Y_OFFSET);
 *  kaleidoscope rotates the whole line into `mirrorCount` radial copies around the
 *  tunnel axis. Reflections/rotations also transform each wall's own rotation so
 *  the mesh mirrors/spins correctly. */
function applySymmetry(base: DesignMeshInstance[], g: DesignShape): DesignMeshInstance[] {
  switch (g.mirror) {
    case 'x':
      return base.concat(base.map(w => ({ ...w, x: -w.x, rotation: -w.rotation })));
    case 'y':
      return base.concat(base.map(w => ({ ...w, y: -w.y, rotation: -w.rotation })));
    case 'kaleidoscope': {
      const n = Math.max(2, Math.round(g.mirrorCount));
      const out: DesignMeshInstance[] = [];
      for (let k = 0; k < n; k++) {
        const a = (k * 2 * Math.PI) / n;
        const ca = Math.cos(a), sa = Math.sin(a);
        const deg = (a * 180) / Math.PI;
        for (const w of base) {
          out.push({
            ...w,
            x: w.x * ca - w.y * sa,
            y: w.x * sa + w.y * ca,
            rotation: w.rotation + deg,
          });
        }
      }
      return out;
    }
    case 'none':
    default:
      return base;
  }
}

/** Total number of obstacles a group actually renders, WITHOUT allocating the
 *  instance array — same arithmetic as sampleDesignShape (base count × the
 *  mirror/kaleidoscope multiplier × the module-repeat count), so the panel can
 *  show a live, accurate total even for large modules. */
/** Synth Riders Quest caps SOLID walls per wall art. Beyond this, the game
 *  auto-renders the whole art as wireframe (cheaper), so the editor mirrors it. */
export const QUEST_SOLID_WALL_CAP = 500;

export function countDesignMeshes(g: DesignShape): number {
  const base = Math.max(1, Math.round(g.count));
  const mirrorMul = g.mirror === 'x' || g.mirror === 'y'
    ? 2
    : g.mirror === 'kaleidoscope' ? Math.max(2, Math.round(g.mirrorCount)) : 1;
  const repeatMul = Math.max(1, Math.round(g.repeatCount || 1));
  const layers = 1 + (g.parts?.length || 0);   // primary shape + every extra part
  return base * mirrorMul * repeatMul * layers;
}

/** The beat span [min, max] a group occupies on the timeline — cheap analytic
 *  form (no instance allocation). Only the z axis / z-module-repeat spread beats;
 *  x/y lines and mirror/kaleidoscope leave the beat unchanged. */
export function designBeatRange(g: DesignShape): [number, number] {
  const n = Math.max(1, Math.round(g.count));
  const half = (n - 1) / 2;
  let min: number, max: number;
  if (g.curve === 'custom') {
    // A custom path spreads in beats by its nodes' z offsets (include 0 so a flat
    // path still spans the anchor beat).
    const pts = (g.customPath && g.customPath.length) ? g.customPath : DEFAULT_CUSTOM_PATH;
    const zs = pts.map(p => p.z);
    min = g.beat + Math.min(0, ...zs);
    max = g.beat + Math.max(0, ...zs);
  } else {
    // Bent-line & 2D-shape presets only spread in beats when the Z axis is checked.
    const lineHalf = resolveAxes(g).includes('z') ? half * g.spacing : 0;
    min = g.beat - lineHalf;
    max = g.beat + lineHalf;
  }
  const rc = Math.max(1, Math.round(g.repeatCount || 1));
  if ((g.repeatAxis ?? 'z') === 'z' && rc > 1) max += (rc - 1) * (g.repeatSpacing ?? 2);
  return [min, max];
}

/** Expand a group into its concrete wall transforms (line + symmetry effect). */
export function sampleDesignShape(g: DesignShape): DesignMeshInstance[] {
  const n = Math.max(1, Math.round(g.count));
  const base: DesignMeshInstance[] = [];
  const half = (n - 1) / 2;
  const axes = resolveAxes(g);
  // Custom curve: pre-sample the smooth path through the control nodes once.
  const isCustom = g.curve === 'custom';
  const isShape = isShapeCurve(g.curve);
  const customPts = isCustom ? sampleCustomPath(g.customPath, n) : null;
  for (let i = 0; i < n; i++) {
    const t = n > 1 ? i / (n - 1) : 0.5;   // 0..1 along the line
    const step = (i - half) * g.spacing;   // distance from centre along the axis

    // Built in LOCAL space (centred on 0,0) — Global Position (originX/Y) is
    // applied ONCE at the very end, after symmetry/repeat, so it translates the
    // whole assembled shape as one rigid unit. Baking it in here instead would
    // make mirror/kaleidoscope reflect/rotate around world (0,0) rather than the
    // group's own moved centre — only the un-mirrored side would track Move X/Y.
    let x = g.spreadX || 0;
    let y = g.spreadY || 0;
    let beat = g.beat + (g.spreadZ || 0);

    if (isCustom && customPts) {
      // Custom node path owns all three local coordinates (x/y + a beat offset);
      // the axis-walk is suppressed and amplitude does not apply (nodes ARE the path).
      const p = customPts[i];
      x += p.x; y += p.y; beat += p.z;
    } else if (isShape) {
      // Parametric 2D shape (circle/spiral): x/y come from the shape × amplitude
      // (radius). The x/y axis-walk is suppressed, but checking the Z axis still
      // advances the beat — extruding the shape through time (ring → cylinder,
      // spiral → helix). A closed circle spreads its points i/n (no overlap at
      // the seam); the spiral runs the full i/(n-1) sweep.
      const pt = g.curve === 'circle' ? (n > 0 ? i / n : 0) : t;
      const s = curveShapeXY(g.curve, pt);
      x += s.x * g.amplitude;
      y += s.y * g.amplitude;
      if (axes.includes('z')) beat += step;
    } else {
      // Bent line (default): one or more axes checked → the line walks diagonally
      // across all of them at once (e.g. X+Z ramps sideways while moving forward
      // in time). The curve adds a perpendicular displacement (an x-line curves
      // in Y; everything else curves in X) — same convention as the single axis.
      if (axes.includes('x')) x += step;
      if (axes.includes('y')) y += step;
      if (axes.includes('z')) beat += step;  // z axis = time (beats)
      const d = curveDisplacement(g.curve, t) * g.amplitude;
      if (axes.includes('x')) y += d;
      else x += d;
    }

    // Taper: the extreme walls scale toward (1 - taperStrength); centre stays 1.
    // taperLength controls how much of each half ramps: L=1 is a full ramp from
    // centre to ends; smaller L keeps the inner (1-L) at full and only the outer
    // L fraction tapers (a shorter / sharper tip).
    const edge = Math.min(t, 1 - t) * 2;   // 0 at the ends, 1 in the middle
    const taperL = Math.max(0.02, g.taperLength ?? 1);
    const ramp = Math.min(1, edge / taperL);
    const taper = g.tip === 'taper' ? (1 - g.taperStrength) + g.taperStrength * ramp : 1;

    // Twist: a graded per-object rotation that ramps 0 → twist along the line,
    // so each successive wall is rotated a little more (a helix / twisted ribbon).
    const twist = t * (g.twist || 0);

    base.push({
      x, y, beat, scale: g.scale * taper, scaleZ: g.scaleZ ?? 1, rotation: g.rotation + twist,
      rotationX: g.rotationX || 0, rotationY: g.rotationY || 0,
    });
  }

  // The "module" = the sampled line + its symmetry effect.
  const module = applySymmetry(base, g);

  // Array the whole module: repeat it `repeatCount` times, each copy offset by
  // `repeatSpacing` along `repeatAxis` (z = beats). Guards keep old drafts (that
  // predate these fields) rendering as a single module.
  const rc = Math.max(1, Math.round(g.repeatCount || 1));
  let out: DesignMeshInstance[];
  if (rc <= 1) {
    out = module;
  } else {
    const gap = g.repeatSpacing ?? 2;
    const axis = g.repeatAxis ?? 'z';
    out = [];
    for (let k = 0; k < rc; k++) {
      const off = k * gap;
      for (const w of module) {
        const c: DesignMeshInstance = { ...w };
        if (axis === 'x') c.x += off;
        else if (axis === 'y') c.y += off;
        else c.beat += off;
        out.push(c);
      }
    }
  }

  // Expand Rotation — spin the WHOLE assembled arrangement (positions +
  // orientation) rigidly around the group's own centre, POST-symmetry so every
  // mirrored/kaleido/repeated copy turns together as one unit. Distinct from the
  // per-wall Rotation X/Y/Z (which orient each mesh in place and get negated by
  // mirror symmetry). Uses THREE's Euler-'XYZ' so it matches the renderer's
  // orientation convention exactly. Z (in-plane spin) is geometrically exact;
  // X/Y tilt the piece into depth (beat treated 1:1 with x/y for the tilt).
  const rrx = g.spreadRotX || 0, rry = g.spreadRotY || 0, rrz = g.spreadRotZ || 0;
  if (rrx || rry || rrz) {
    _spreadEuler.set((rrx * Math.PI) / 180, (rry * Math.PI) / 180, (rrz * Math.PI) / 180);
    out = out.map(w => {
      _spreadVec.set(w.x, w.y, w.beat - g.beat).applyEuler(_spreadEuler);
      return {
        ...w,
        x: _spreadVec.x,
        y: _spreadVec.y,
        beat: g.beat + _spreadVec.z,
        rotation: w.rotation + rrz,
        rotationX: w.rotationX + rrx,
        rotationY: w.rotationY + rry,
      };
    });
  }

  // Global Position — shift the WHOLE assembled shape (every mirrored/kaleido/
  // repeated copy) by the same amount, so it always moves together as one unit.
  out = out.map(w => ({ ...w, x: w.x + g.originX, y: w.y + g.originY }));

  // Global Rotation — orient the whole PLACED wall art in 3D, rotating rigidly
  // around its placed centre (originX, originY, g.beat) as the very last step.
  // Companion to Global Position's Move X/Y/Z: Move places it, Rotate aims it.
  // (Expand rotation, above, spins the pattern around its LOCAL build centre
  // before placement; this one turns the finished, positioned piece.)
  const grx = g.originRotX || 0, gry = g.originRotY || 0, grz = g.originRotZ || 0;
  if (grx || gry || grz) {
    _spreadEuler.set((grx * Math.PI) / 180, (gry * Math.PI) / 180, (grz * Math.PI) / 180);
    out = out.map(w => {
      _spreadVec.set(w.x - g.originX, w.y - g.originY, w.beat - g.beat).applyEuler(_spreadEuler);
      return {
        ...w,
        x: g.originX + _spreadVec.x,
        y: g.originY + _spreadVec.y,
        beat: g.beat + _spreadVec.z,
        rotation: w.rotation + grz,
        rotationX: w.rotationX + grx,
        rotationY: w.rotationY + gry,
      };
    });
  }

  return out;
}

/** The PRIMARY shape's instances = the base slots plus the primary's own local
 *  offset (offsetX/Y/Z). Parts ride on the un-offset base (sampleDesignShape), so
 *  moving the primary shifts ONLY shape 1 — letting you align it against the
 *  parts (e.g. centre a mirror) rather than moving the whole composite. */
export function samplePrimaryInstances(g: DesignShape): DesignMeshInstance[] {
  const ox = g.offsetX || 0, oy = g.offsetY || 0, oz = g.offsetZ || 0;
  const base = sampleDesignShape(g);
  if (!ox && !oy && !oz) return base;
  return base.map(inst => ({ ...inst, x: inst.x + ox, y: inst.y + oy, beat: inst.beat + oz }));
}

/** Expand a PART into its concrete wall transforms — rides at the exact same
 *  repeat/effects/module slots as the primary shape (so it stays visually
 *  attached through every Frequency/Effects change), offset by the part's own
 *  local (offsetX, offsetY, offsetBeat) and styled with its own scale/rotation. */
export function sampleMeshInstances(g: DesignShape, part: DesignMesh): DesignMeshInstance[] {
  // DISCONNECTED: sample the part as its OWN standalone pattern using its
  // Frequency/Effects/Expand overrides (fall back to the group's). It still sits
  // at the group's Global Position + the part's own local offset, and carries
  // the part's own scale/scaleZ/rotation.
  if (g.shapesIndependent) {
    const pg: DesignShape = {
      ...g,
      shape: part.shape,
      scale: part.scale,
      scaleZ: part.scaleZ ?? 1,
      rotation: part.rotation,
      rotationX: part.rotationX || 0,
      rotationY: part.rotationY || 0,
      axes: part.axes ?? g.axes,
      count: part.count ?? g.count,
      spacing: part.spacing ?? g.spacing,
      curve: part.curve ?? g.curve,
      amplitude: part.amplitude ?? g.amplitude,
      customPath: part.customPath ?? g.customPath,
      tip: part.tip ?? g.tip,
      taperStrength: part.taperStrength ?? g.taperStrength,
      taperLength: part.taperLength ?? g.taperLength,
      twist: part.twist ?? g.twist,
      mirror: part.mirror ?? g.mirror,
      mirrorCount: part.mirrorCount ?? g.mirrorCount,
      repeatCount: part.repeatCount ?? g.repeatCount,
      repeatSpacing: part.repeatSpacing ?? g.repeatSpacing,
      repeatAxis: part.repeatAxis ?? g.repeatAxis,
      spreadX: part.spreadX ?? g.spreadX,
      spreadY: part.spreadY ?? g.spreadY,
      spreadZ: part.spreadZ ?? g.spreadZ,
      spreadRotX: part.spreadRotX ?? g.spreadRotX,
      spreadRotY: part.spreadRotY ?? g.spreadRotY,
      spreadRotZ: part.spreadRotZ ?? g.spreadRotZ,
      // the part's local offset becomes the primary offset of its virtual group,
      // so it stays where it's placed relative to the composite.
      offsetX: part.offsetX,
      offsetY: part.offsetY,
      offsetZ: part.offsetBeat,
      parts: [],
    };
    return samplePrimaryInstances(pg);
  }
  // ANCHORED (default): ride the primary's sampled slots + the part's offset.
  return sampleDesignShape(g).map(inst => ({
    x: inst.x + part.offsetX,
    y: inst.y + part.offsetY,
    beat: inst.beat + part.offsetBeat,
    scale: inst.scale * part.scale,
    scaleZ: inst.scaleZ * (part.scaleZ ?? 1),
    rotation: inst.rotation + part.rotation,
    rotationX: inst.rotationX + (part.rotationX || 0),
    rotationY: inst.rotationY + (part.rotationY || 0),
  }));
}

// ── Animation ────────────────────────────────────────────────────────────────

export function designAnimActive(g: { anim?: DesignAnimConfig }): boolean {
  return !!g.anim && g.anim.preset !== 'none';
}

/** A fresh animation config for a preset, with sensible defaults so it looks good
 *  immediately. `prevSync` carries the user's Time/Music/Notes choice across
 *  preset swaps (so toggling presets doesn't reset how it reacts). */
export function makeDesignAnim(preset: DesignAnimPreset, prevSync?: DesignAnimSync): DesignAnimConfig {
  const T: Record<DesignAnimPreset, { sync: DesignAnimSync; speed: number; amount: number; phase: number }> = {
    none:      { sync: 'time',  speed: 1,   amount: 0,   phase: 1 },
    pulse:     { sync: 'music', speed: 2,   amount: 0.4, phase: 0 },
    breathe:   { sync: 'time',  speed: 1,   amount: 0.4, phase: 0 },
    shockwave: { sync: 'notes', speed: 2,   amount: 0.8, phase: 1 },
    flicker:   { sync: 'music', speed: 3,   amount: 0.5, phase: 0.5 },
    wave:      { sync: 'time',  speed: 2,   amount: 0.6, phase: 1 },
    tentacle:  { sync: 'time',  speed: 1.6, amount: 0.9, phase: 0.7 },
    ripple:    { sync: 'music', speed: 2.2, amount: 0.7, phase: 1.1 },
    jelly:     { sync: 'notes', speed: 3,   amount: 0.6, phase: 0.8 },
    spin:      { sync: 'time',  speed: 1,   amount: 1,   phase: 0 },
    orbit:     { sync: 'time',  speed: 0.6, amount: 1,   phase: 0 },
    twist:     { sync: 'time',  speed: 1.4, amount: 0.7, phase: 0.6 },
    sway:      { sync: 'time',  speed: 1.2, amount: 0.8, phase: 0.3 },
  };
  const d = T[preset];
  return {
    preset, sync: prevSync ?? d.sync, speed: d.speed, amount: d.amount, phase: d.phase,
    // Beat-reaction defaults approximate the old sharp impulse (near-instant in,
    // ~half-beat decelerating out); Uniform distribution = no behaviour change.
    attack: 0.04, release: 0.45, easeIn: 1.5, easeOut: 2.2, dist: 'uniform', stagger: 0.5,
    tentacleMode: 'sway',
  };
}

/** Shaped per-hit reaction envelope for Notes sync: an accelerating rise over
 *  `attack` beats (ease-in) then a decelerating fall over `release` beats
 *  (ease-out). `dt` = beats since the note hit (may be pre-offset by a segment's
 *  stagger, so a negative dt — not yet triggered — returns 0). Returns 0..1. */
export function noteReactionEnvelope(dt: number, attack: number, release: number, easeIn: number, easeOut: number): number {
  if (dt < 0) return 0;
  const a = Math.max(0, attack);
  const rel = Math.max(1e-4, release);
  if (dt < a) {
    const x = a <= 1e-4 ? 1 : dt / a;
    return Math.pow(x, Math.max(0.1, easeIn));      // accelerating rise 0 → 1
  }
  const x = (dt - a) / rel;                          // 0 → 1 across the release
  if (x >= 1) return 0;
  return Math.pow(1 - x, Math.max(0.1, easeOut));    // decelerating fall 1 → 0
}

/** Per-wall time offset (beats) for a staggered reaction distribution — the beat
 *  reaches each segment at a different time so it reacts by segments instead of
 *  all at once. Each wall has a stable "rank" u∈[0,1): `random` = a scatter hash,
 *  `sequence` = along the generation order, `radial` = distance from the centre.
 *
 *  When `front` is given (a per-beat position that advances by the golden ratio,
 *  so it lands in the largest not-yet-covered gap each beat), the offset is the
 *  circular distance from that moving front — so a DIFFERENT band of segments
 *  leads each pulse and coverage rotates organically instead of resetting to the
 *  same pattern every time. With no `front` (e.g. Music) it's a stable ripple. */
export function designSegmentOffset(dist: DesignAnimDist, i: number, n: number, dx: number, dy: number, stagger: number, front?: number): number {
  if (!stagger || dist === 'uniform') return 0;
  const u = designSegmentRank(dist, i, n, dx, dy);         // this wall's place in the distribution
  if (front == null) return u * stagger;                 // stable ripple
  let d = Math.abs(u - front);
  d = Math.min(d, 1 - d);                                 // circular distance 0..0.5
  return d * 2 * stagger;                                 // near the moving front → reacts first
}

/** A wall's stable "rank" u∈[0,1) within a staggered distribution — `random` = a
 *  scatter hash of its offset from the centre, `sequence` = along the generation
 *  order, `radial` = distance from the centre. Shared by the renderer AND the
 *  .synth bake (written per wall as `animRank`) so Unity can reproduce the same
 *  per-segment stagger the editor shows. Uniform has no rank (0). */
export function designSegmentRank(dist: DesignAnimDist, i: number, n: number, dx: number, dy: number): number {
  if (dist === 'random') { const s = Math.sin(dx * 12.9898 + dy * 78.233) * 43758.5453; return s - Math.floor(s); }
  if (dist === 'sequence') return n > 1 ? i / (n - 1) : 0;
  if (dist === 'radial') return Math.min(1, Math.hypot(dx, dy) / 4);
  return 0;
}

/** Which presets read the spatial `phase`/Spread slider (a traveling wave across
 *  the art) — the uniform scale/rotation presets ignore it. */
export function designAnimUsesSpread(preset: DesignAnimPreset): boolean {
  return preset !== 'pulse' && preset !== 'breathe' && preset !== 'spin' && preset !== 'orbit';
}

/** Apply a preset's live deformation to one wall, writing the animated transform
 *  into `out` (a reused instance — no per-frame allocation). Pure: `t` is the
 *  advancing phase (elapsed × speed), `drive` (0..1) is the reactive intensity
 *  (music energy / note impulse / 1 for free clock), `pulse` (0..1) is the
 *  scale-pump signal, and (cx,cy,cb) is the art's placed centre. */
export function applyDesignAnim(
  base: DesignMeshInstance, cfg: DesignAnimConfig,
  t: number, drive: number, pulse: number,
  cx: number, cy: number, cb: number,
  out: DesignMeshInstance,
): void {
  // Start from the static pose.
  out.x = base.x; out.y = base.y; out.beat = base.beat;
  out.scale = base.scale; out.scaleZ = base.scaleZ;
  out.rotation = base.rotation; out.rotationX = base.rotationX; out.rotationY = base.rotationY;

  const A = cfg.amount;
  const K = cfg.phase;
  const dx = base.x - cx, dy = base.y - cy, db = base.beat - cb;
  const r = Math.hypot(dx, dy);
  // Flow presets: music/notes intensify the motion; 'time' runs near full.
  const mix = 0.35 + 0.65 * drive;

  switch (cfg.preset) {
    case 'pulse':
      out.scale = base.scale * Math.max(0.04, 1 + A * pulse);
      break;
    case 'breathe':
      out.scale = base.scale * Math.max(0.04, 1 + A * 0.5 * Math.sin(t) * (0.6 + 0.4 * drive));
      break;
    case 'shockwave': {
      // A gaussian ring sweeping outward (period ~6 units), pumped by the driver.
      const ringR = (t * 1.5) % 6;
      const ring = Math.exp(-Math.pow((r - ringR) * 1.4, 2));
      out.scale = base.scale * (1 + A * ring * (0.4 + 0.9 * pulse));
      break;
    }
    case 'flicker': {
      const f = 0.5 + 0.5 * Math.sin(t * 6 + r * 5 + dx * 3);
      out.scale = base.scale * Math.max(0.04, 1 - A * 0.5 * (1 - pulse) + A * 0.4 * f * pulse);
      break;
    }
    case 'wave':
      out.y = base.y + A * mix * Math.sin(t + (dx + db) * K);
      break;
    case 'tentacle': {
      const len = Math.abs(db) + Math.abs(dx) * 0.4 + r * 0.2;
      if (cfg.tentacleMode === 'radial') {
        // Expand / contract radially from the centre — the walls open and close
        // symmetrically (each moves along its own outward direction, tips reaching
        // furthest). Spread (K) turns the uniform open/close into a wave that
        // ripples outward; Spread 0 = every wall opens & closes together.
        const osc = A * mix * Math.sin(t + len * K);
        const grow = 0.4 + len * 0.6;                 // tips reach further than the base
        const inv = r > 1e-3 ? 1 / r : 0;             // radial unit dir (0 at the centre)
        out.x = base.x + dx * inv * osc * grow;
        out.y = base.y + dy * inv * osc * grow;
        out.rotation = base.rotation + osc * 12;
      } else {
        // Sway — whip-like side to side (default); segments tilt into the curl.
        const bend = A * mix * Math.sin(t + len * K);
        out.x = base.x + bend * (0.4 + len * 0.5);
        out.y = base.y + A * mix * 0.3 * Math.cos(t * 0.8 + len * K);
        out.rotation = base.rotation + bend * 25;
      }
      break;
    }
    case 'ripple': {
      // Concentric wave pushing each wall along its radial direction.
      const w = A * mix * Math.sin(t - r * K);
      const inv = r > 1e-3 ? 1 / r : 0;
      out.x = base.x + dx * inv * w;
      out.y = base.y + dy * inv * w;
      break;
    }
    case 'jelly': {
      out.scale = base.scale * Math.max(0.04, 1 + A * 0.3 * mix * Math.sin(t - r * K));
      out.x = base.x + A * 0.22 * mix * Math.sin(t * 1.3 - db * K);
      out.y = base.y + A * 0.22 * mix * Math.cos(t * 1.1 - dx * K);
      break;
    }
    case 'spin':
      out.rotation = base.rotation + t * 50 * mix;
      break;
    case 'orbit': {
      const ang = t * mix;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      out.x = cx + dx * ca - dy * sa;
      out.y = cy + dx * sa + dy * ca;
      out.rotation = base.rotation + (ang * 180) / Math.PI;
      break;
    }
    case 'twist': {
      const tw = A * mix * Math.sin(t);
      const ang = tw * db * 0.5;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      out.x = cx + dx * ca - dy * sa;
      out.y = cy + dx * sa + dy * ca;
      out.rotation = base.rotation + tw * db * 20;
      break;
    }
    case 'sway': {
      // Gentle whole-art tilt back and forth, like swaying in the wind.
      const ang = A * 0.18 * mix * Math.sin(t);
      const ca = Math.cos(ang), sa = Math.sin(ang);
      out.x = cx + dx * ca - dy * sa;
      out.y = cy + dx * sa + dy * ca;
      out.rotation = base.rotation + (ang * 180) / Math.PI;
      break;
    }
    case 'none':
    default:
      break;
  }
}

// ── Bake decor → .synth obstacles ────────────────────────────────────────────

/** One baked obstacle in the shape the backend `ObstacleIn` / exporter expect.
 *  The `shape`/`color`/`color_end`/`gradient` fields are EXTRA keys the stock
 *  game ignores — they're written into the .synth JSON so a modded Unity build
 *  can pick the right mesh per wall and tint it with the designer's colour. */
export interface BakedObstacle {
  time_ms: number;
  position: [number, number, number];
  z_rotation: number;
  has_rotation: boolean;
  initialized: boolean;
  shape: ObstacleShape;      // exact designer shape (regular/diagonal/center/square/triangle)
  color: string;             // resolved per-wall hex (gradient already applied)
  color_end: string;         // gradient end hex ('' when the wall isn't a gradient)
  gradient: boolean;         // true when this wall belongs to a gradient sequence
  // ── Per-wall transform extras the .synth's stock structs drop (only position +
  //    zRotation survive there) — a modded build reads these to size/tilt each
  //    wall exactly like the editor. `scale` carries the TAPER (ends scale down). ──
  scale?: number;            // uniform scale multiplier (base × taper × part scale)
  scale_z?: number;          // extra depth (local-Z) multiplier; omitted when 1
  rotation_x?: number;       // X tilt (deg); omitted when 0
  rotation_y?: number;       // Y turn (deg); omitted when 0
  wireframe?: boolean;       // true when this wall's shape renders as wireframe
  // ── Animation (only when this wall's layer is animated) — extra JSON the game
  //    reads to drive the same live motion the editor shows. `anim` is the full
  //    config, `anim_center` the pivot [x,y,beat], `anim_rank` this wall's 0..1
  //    place in the distribution (for the per-segment stagger). See applyDesignAnim. ──
  anim?: DesignAnimConfig;
  anim_center?: [number, number, number];
  anim_rank?: number;
}

/** A baked Slide obstacle — same as BakedObstacle plus the slide type/lane the
 *  regular/diagonal/center designer shapes map onto in the .synth `Slides` array. */
export interface BakedSlide extends BakedObstacle {
  slide_type: number;
  wall_type: string;
}

/** Per-instance gradient parameter t∈[0,1] for a layer, given the distribution
 *  direction. 'order' = generation index; x/y/z = normalized world position on
 *  that axis; 'radial' = normalized distance from the layer's centroid. Shared
 *  by the renderer (DesignShapes) and the .synth bake so the exported colour
 *  equals what the Shape Designer shows. */
export function gradientParams(instances: DesignMeshInstance[], dir: DesignGradientDir = 'order'): number[] {
  const n = instances.length;
  if (n <= 1) return instances.map(() => 0);
  if (dir === 'order') return instances.map((_, i) => i / (n - 1));
  let vals: number[];
  if (dir === 'x') vals = instances.map(w => w.x);
  else if (dir === 'y') vals = instances.map(w => w.y);
  else if (dir === 'z') vals = instances.map(w => w.beat);
  else { // radial — distance from the layer's centroid
    const cx = instances.reduce((s, w) => s + w.x, 0) / n;
    const cy = instances.reduce((s, w) => s + w.y, 0) / n;
    vals = instances.map(w => Math.hypot(w.x - cx, w.y - cy));
  }
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min;
  return vals.map(v => (range < 1e-6 ? 0 : (v - min) / range));
}

// Per-wall gradient colour — MUST match DesignShapes.tsx's renderer exactly so the
// baked in-game colour equals what the Shape Designer shows.
const BAKE_GRADIENT_STOPS = 16;
function bakeLerpHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.replace('#', ''), 16);
  const pb = parseInt(b.replace('#', ''), 16);
  const ar = (pa >> 16) & 255, ag = (pa >> 8) & 255, ab = pa & 255;
  const br = (pb >> 16) & 255, bg = (pb >> 8) & 255, bb = pb & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return '#' + ((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1);
}

/** slideType + wall_type a regular/diagonal/center shape maps onto. Left/right
 *  lanes are chosen by the wall's X sign (matches DEFAULT_OBSTACLE_CONFIGS). */
function bakeSlideType(shape: ObstacleShape, x: number): [number, string] {
  if (shape === 'diagonal') return x > 0 ? [2, 'angle_right'] : [4, 'angle_left'];
  if (shape === 'center')   return [3, 'center'];
  return x > 0 ? [0, 'wall_right'] : [1, 'wall_left']; // 'regular'
}

/** Bake all wall arts into flat obstacle lists for the .synth. Each sampled wall
 *  (primary + every visible part) becomes one obstacle at its world (x, y) with
 *  its Z rotation, routed to the array matching its designer shape:
 *    square → Squares, triangle → Triangles, regular/diagonal/center → Slides.
 *  Every entry also carries the exact shape + resolved colour (+ gradient end)
 *  as extra JSON fields for a modded game to read. Scale / X-Y rotation are
 *  dropped (the .synth carries only position + zRotation). Hidden shapes are
 *  skipped. `beat` → `time_ms` via the map BPM (chart-relative, offset-free). */
export function bakeDesignToObstacles(
  groups: DesignShape[], bpm: number,
): { squares: BakedObstacle[]; triangles: BakedObstacle[]; slides: BakedSlide[] } {
  const squares: BakedObstacle[] = [];
  const triangles: BakedObstacle[] = [];
  const slides: BakedSlide[] = [];
  const beatToMs = 60000 / (bpm || 120);

  const round4 = (x: number) => Math.round(x * 1e4) / 1e4;
  const addLayer = (
    shape: ObstacleShape, color: string, gradient: boolean, colorEnd: string,
    instances: DesignMeshInstance[], dir: DesignGradientDir,
    anim: DesignAnimConfig | null, center: [number, number, number], wireframe: boolean,
  ) => {
    const n = instances.length;
    const endCol = colorEnd || color;
    const ts = gradient ? gradientParams(instances, dir) : null;
    const dist = anim?.dist ?? 'uniform';
    const staggered = !!anim && dist !== 'uniform' && !!anim.stagger;
    for (let i = 0; i < n; i++) {
      const inst = instances[i];
      let col = color;
      if (gradient && n > 1 && ts) {
        const step = Math.round(ts[i] * BAKE_GRADIENT_STOPS) / BAKE_GRADIENT_STOPS;
        col = bakeLerpHex(color, endCol, step);
      }
      const ob: BakedObstacle = {
        time_ms: inst.beat * beatToMs,
        position: [inst.x, inst.y, 0],
        z_rotation: inst.rotation,
        has_rotation: true,
        initialized: true,
        shape,
        color: col,
        color_end: gradient ? endCol : '',
        gradient: !!gradient,
        // Per-wall transform — `scale` carries the taper (ends scale down); the
        // rest only when non-default so static/untilted walls stay compact.
        scale: round4(inst.scale),
      };
      if ((inst.scaleZ ?? 1) !== 1) ob.scale_z = round4(inst.scaleZ);
      if (inst.rotationX) ob.rotation_x = round4(inst.rotationX);
      if (inst.rotationY) ob.rotation_y = round4(inst.rotationY);
      if (wireframe) ob.wireframe = true;
      // Per-wall animation extras — carry the whole art's live-motion params onto
      // each wall so the game can reproduce it (the pivot + this wall's stagger
      // rank make radial/segmented reactions replayable without the sampler).
      if (anim) {
        ob.anim = anim;
        ob.anim_center = center;
        ob.anim_rank = staggered ? designSegmentRank(dist, i, n, inst.x - center[0], inst.y - center[1]) : 0;
      }
      if (shape === 'square') squares.push(ob);
      else if (shape === 'triangle') triangles.push(ob);
      else {
        const [slide_type, wall_type] = bakeSlideType(shape, inst.x);
        slides.push({ ...ob, slide_type, wall_type });
      }
    }
  };

  for (const g of groups) {
    const dir = g.gradientDir ?? 'order';
    // Resolve the animation per layer, mirroring the renderer: anchored shapes
    // share g.anim around the group centre; disconnected shapes each use their own
    // anim (falling back to g.anim) around their own placed centre.
    const groupCenter: [number, number, number] = [g.originX, g.originY, g.beat];
    const disc = !!g.shapesIndependent;
    const primaryAnim = designAnimActive(g) ? g.anim! : null;
    const resolveAnim = (a?: DesignAnimConfig) => (a && a.preset !== 'none') ? a : null;
    if (!g.hidden) addLayer(g.shape, g.color, g.gradient, g.colorEnd, samplePrimaryInstances(g), dir, primaryAnim, groupCenter, g.style === 'wireframe');
    for (const part of g.parts || []) {
      if (part.hidden) continue;
      const partAnim = disc ? resolveAnim(part.anim ?? g.anim) : primaryAnim;
      const partCenter: [number, number, number] = disc
        ? [g.originX + part.offsetX, g.originY + part.offsetY, g.beat + part.offsetBeat]
        : groupCenter;
      addLayer(part.shape, part.color, part.gradient || false, part.colorEnd || '', sampleMeshInstances(g, part), dir, partAnim, partCenter, part.style === 'wireframe');
    }
  }
  return { squares, triangles, slides };
}

// Curated vivid neon palette — every entry pops against the dark stage and reads
// well through the glow/outline material. A fresh wall art picks one at random.
const NEON_PALETTE = [
  '#00E5FF', // cyan
  '#22D3EE', // sky
  '#2979FF', // electric blue
  '#8B5CFF', // violet
  '#B14BFF', // purple
  '#E040FB', // magenta
  '#FF2D95', // hot pink
  '#FF4FD8', // pink
  '#FF5C5C', // neon red
  '#FF7A18', // orange
  '#FFD400', // yellow
  '#A3FF12', // lime
  '#39FF14', // green
  '#00FFA3', // mint
];
let _lastNeon = -1;
/** A random neon-friendly colour (avoids repeating the immediately-previous one),
 *  so each new wall art comes up in a fresh, vivid colour by default. */
export function randomNeonColor(): string {
  let i = Math.floor(Math.random() * NEON_PALETTE.length);
  if (i === _lastNeon) i = (i + 1) % NEON_PALETTE.length;
  _lastNeon = i;
  return NEON_PALETTE[i];
}

/** A neon colour that isn't already used in this shape — so every added mesh
 *  reads as a distinct colour. Falls back to a random neon once the palette is
 *  exhausted (more meshes than palette entries). */
export function distinctNeonColor(used: Iterable<string>): string {
  const usedSet = new Set(Array.from(used, c => (c || '').toLowerCase()));
  const avail = NEON_PALETTE.filter(c => !usedSet.has(c.toLowerCase()));
  if (avail.length === 0) return randomNeonColor();
  return avail[Math.floor(Math.random() * avail.length)];
}

/** A fresh extra part for a wall art — offset a bit to the side by default so
 *  it's immediately visible next to the primary shape instead of overlapping it. */
export function makeDesignMesh(shape: ObstacleShape, color: string): DesignMesh {
  return {
    id: `part-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    shape, color, gradient: false, colorEnd: '#00E5FF', style: 'solid',
    scale: 1, scaleZ: 1, rotation: 0, rotationX: 0, rotationY: 0, offsetX: 0.5, offsetY: 0, offsetBeat: 0,
    hidden: false,
  };
}

/** A fresh group with sensible defaults, centred at the given beat. Starts as a
 *  SINGLE obstacle (count: 1) — Frequency is opt-in, not a surprise 5-wall line.
 *  The color defaults to the current obstacle fill colour (passed by the store)
 *  so new decor matches the real obstacles out of the box. */
export function makeDesignShape(beat: number, color = '#4444ff'): DesignShape {
  return {
    id: `decor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    name: '',
    beat,
    shape: 'regular',
    axes: ['x'],
    count: 1,
    spacing: 0.9,
    curve: 'line',
    amplitude: 1,
    tip: 'straight',
    taperStrength: 0.6,
    taperLength: 1,
    mirror: 'none',
    mirrorCount: 6,
    repeatCount: 1,
    repeatSpacing: 2,
    repeatAxis: 'z',
    color,
    gradient: false,
    colorEnd: '#00E5FF',
    // Combine · Meshes OFF by default: extra meshes start DISCONNECTED (each gets
    // its own Frequency/Effects/Expand). The panel can anchor them together.
    shapesIndependent: true,
    scale: 0.6,
    scaleZ: 1,
    rotation: 0,
    rotationX: 0,
    rotationY: 0,
    twist: 0,
    offsetX: 0,
    offsetY: 0,
    offsetZ: 0,
    originX: 0,
    originY: 0.6,
    spreadX: 0,
    spreadY: 0,
    spreadZ: 0,
    spreadRotX: 0,
    spreadRotY: 0,
    spreadRotZ: 0,
    originRotX: 0,
    originRotY: 0,
    originRotZ: 0,
    parts: [],
    linkedShape: false,
    linkedColor: false,
    hidden: false,
  };
}

// ── Wall art library (cross-project saved arts, like the pattern library) ──────

/** A wall art the user saved to reuse in ANY project. Holds a self-contained deep
 *  copy of the group; its placement (beat) is re-set to the playhead on insert. */
export interface SavedShape {
  id: string;
  name: string;
  savedAt: number;
  group: DesignShape;
  // Server-backed library fields (absent on legacy localStorage-only entries).
  owner?: string;
  visibility?: 'private' | 'public';
  is_owner?: boolean;
}

function cloneGroup(g: DesignShape): DesignShape {
  return JSON.parse(JSON.stringify(g));
}

/** Snapshot a group into a reusable library entry (deep copy; fresh entry id). */
export function makeSavedShape(g: DesignShape, name: string): SavedShape {
  return {
    id: `lib-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    name: (name || '').trim() || 'Wall Art',
    savedAt: Date.now(),
    group: cloneGroup(g),
  };
}

/** Instantiate a saved library art into a map — a deep copy with a fresh group id,
 *  placed at `beat` (the current playhead). Keeps shape/parts/anim/curve/etc. */
export function instantiateSavedShape(saved: SavedShape, beat: number): DesignShape {
  const g = cloneGroup(saved.group);
  g.id = `decor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  g.beat = Math.max(0, beat);
  g.name = saved.name;
  return g;
}
